import { Modal, Pressable, StyleSheet, Text } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useTranslation } from 'react-i18next';

import { Icon, type IconName } from '../index';
import { useTheme } from './theme';

// Bottom action-sheet for managing an EXISTING superset (inbox 963ca6f2, batch
// 2). Reachable from the ☰ on the group header in BOTH loggers. One discoverable
// home for: save-as-template, add a member, reorder (up/down on the shown
// member), remove a member, dissolve. (Rename removed AP-0n — the superset has
// no visible name any more, so there was nothing to rename.)
export function SupersetMenuSheet({
  visible,
  groupLabel,
  canMoveUp,
  canMoveDown,
  onSaveTemplate,
  onAddExercise,
  onMoveUp,
  onMoveDown,
  onRemoveMember,
  onSplit,
  onClose,
}: {
  visible: boolean;
  groupLabel: string;
  canMoveUp: boolean;
  canMoveDown: boolean;
  /** Absent ⇒ the row is not rendered at all (PE-26). ⛔ Never disabled-but-
   *  present: a control that announces itself and does nothing is the defect
   *  this optionality exists to prevent. */
  onSaveTemplate?: () => void;
  onAddExercise: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
  onRemoveMember: () => void;
  onSplit: () => void;
  onClose: () => void;
}) {
  const theme = useTheme();
  const { t } = useTranslation();
  const styles = makeStyles(theme);

  const rows: Array<{
    key: string;
    label: string;
    glyph?: string;
    iconName?: IconName;
    fill?: boolean;
    onPress: () => void;
    disabled?: boolean;
    danger?: boolean;
  }> = [
    ...(onSaveTemplate
      ? [{ key: 'template', iconName: 'star' as IconName, fill: true, label: t('mobile.superset.menuSaveTemplate', { defaultValue: 'Save as template' }), onPress: onSaveTemplate }]
      : []),
    { key: 'add', iconName: 'plus', label: t('mobile.superset.menuAddExercise', { defaultValue: 'Add exercise' }), onPress: onAddExercise },
    { key: 'up', iconName: 'chevron-up', label: t('mobile.superset.menuMoveUp', { defaultValue: 'Move up' }), onPress: onMoveUp, disabled: !canMoveUp },
    { key: 'down', iconName: 'chevron-down', label: t('mobile.superset.menuMoveDown', { defaultValue: 'Move down' }), onPress: onMoveDown, disabled: !canMoveDown },
    { key: 'remove', iconName: 'trash', label: t('mobile.superset.menuRemoveMember', { defaultValue: 'Remove this exercise' }), onPress: onRemoveMember },
    // ✂️ has no equivalent in the icon set — keep as text glyph
    { key: 'split', iconName: 'scissors', label: t('mobile.superset.menuSplit', { defaultValue: 'Dissolve superset' }), onPress: onSplit, danger: true },
  ];

  return (
    <Modal visible={visible} transparent animationType="slide" onRequestClose={onClose}>
      <Pressable style={styles.backdrop} onPress={onClose}>
        <Pressable style={styles.sheetWrap} onPress={() => {}}>
          <SafeAreaView edges={['bottom']} style={styles.sheet}>
            <Text style={styles.title} numberOfLines={1}>
              {groupLabel}
            </Text>
            {rows.map((r) => (
              <Pressable
                key={r.key}
                accessibilityRole="button"
                disabled={r.disabled}
                onPress={() => {
                  if (r.disabled) return;
                  r.onPress();
                }}
                style={({ pressed }) => [
                  styles.row,
                  pressed && !r.disabled && styles.rowPressed,
                ]}
              >
                {r.iconName ? (
                  <Icon
                    name={r.iconName}
                    size={theme.typography.sizes.lg}
                    color={r.disabled ? theme.palette.ink3 : r.danger ? theme.palette.danger : theme.palette.ink}
                    fill={r.fill ? (r.disabled ? theme.palette.ink3 : theme.palette.ink) : undefined}
                  />
                ) : (
                  <Text style={styles.rowGlyph}>{r.glyph}</Text>
                )}
                <Text
                  style={[
                    styles.rowLabel,
                    r.danger && styles.rowLabelDanger,
                    r.disabled && styles.rowLabelDisabled,
                  ]}
                >
                  {r.label}
                </Text>
              </Pressable>
            ))}
            <Pressable accessibilityRole="button" onPress={onClose} style={styles.cancel}>
              <Text style={styles.cancelText}>
                {t('mobile.logger.cancel', { defaultValue: 'Cancel' })}
              </Text>
            </Pressable>
          </SafeAreaView>
        </Pressable>
      </Pressable>
    </Modal>
  );
}

function makeStyles(theme: ReturnType<typeof useTheme>) {
  const { palette: p, spacing: s, radius: r, typography: t } = theme;
  return StyleSheet.create({
    backdrop: { flex: 1, backgroundColor: '#0008', justifyContent: 'flex-end' },
    sheetWrap: { width: '100%' },
    sheet: {
      backgroundColor: p.bg,
      borderTopLeftRadius: r.r3,
      borderTopRightRadius: r.r3,
      paddingHorizontal: s.s5,
      paddingTop: s.s4,
      gap: s.s1,
    },
    title: {
      color: p.ink3,
      fontSize: t.sizes.xs,
      fontWeight: '700',
      letterSpacing: 0.6,
      textTransform: 'uppercase',
      paddingBottom: s.s2,
    },
    row: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: s.s4,
      minHeight: 44,
      paddingHorizontal: s.s2,
    },
    rowPressed: { backgroundColor: p.surface2, borderRadius: r.r3 },
    rowGlyph: { fontSize: t.sizes.lg, width: 28, textAlign: 'center' },
    rowLabel: { color: p.ink, fontSize: t.sizes.base, fontWeight: '600' },
    rowLabelDanger: { color: p.danger },
    rowLabelDisabled: { color: p.ink3 },
    cancel: {
      marginTop: s.s2,
      marginBottom: s.s3,
      minHeight: 44,
      alignItems: 'center',
      justifyContent: 'center',
      borderTopWidth: StyleSheet.hairlineWidth,
      borderTopColor: p.border,
    },
    cancelText: { color: p.brand500, fontSize: t.sizes.base, fontWeight: '700' },
  });
}
